import { type Time as TimeValue, parseTime } from '@internationalized/date';
import React, { type ReactNode, useCallback, useRef } from 'react';
import {
  TimeField as TimeFieldRAC,
  DateInput as DateInputRAC,
  DateSegment as DateSegmentRAC,
  type DateSegmentProps,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';

import { type TimeInputFieldProps } from './time-input-field-props';

type DateSegment = DateSegmentProps['segment'];

type TimeInputProps = {
  children: ReactNode;
};

const TimeInput = ({ children }: TimeInputProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    onBlur,
    onFocus,
    use24HourFormat,
    FieldLayout,
    hasError,
  } = useFieldPropsV2<TimeInputFieldProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();

  const handleChange = useCallback(
    (timeValue: TimeValue | null) => {
      const stringValue = timeValueToString(timeValue);
      onChange(stringValue);
    },
    [onChange],
  );

  const labelElement = findChildOfType(children, TimeInput.Label);
  const timeElement = findChildOfType(children, TimeInput.Time);
  const descriptionElement = findChildOfType(children, TimeInput.Description);
  const errorElement = findChildOfType(children, TimeInput.Error);

  const timeValue = stringToTimeValue(value);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <TimeFieldRAC
          id={inputId}
          aria-describedby={ariaDescribedBy}
          value={timeValue}
          onChange={handleChange}
          isDisabled={disabled}
          isInvalid={hasError}
          isRequired={required}
          onBlur={onBlur}
          onFocus={onFocus}
          hourCycle={use24HourFormat ? 24 : 12}
        >
          {timeElement}
        </TimeFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type TimeProps = {
  children: ReactNode;
  className?: string;
};

const Time = ({ children, className }: TimeProps) => {
  const { hasError } = useFieldPropsV2<TimeInputFieldProps>();

  const hourElement = findChildOfType(children, Time.Hour);
  const minuteElement = findChildOfType(children, Time.Minute);
  const periodElement = findChildOfType(children, Time.Period);

  return (
    <DateInputRAC className={className} data-invalid={hasError}>
      {(segment) => {
        switch (segment.type) {
          case 'hour':
            return (
              applyInternalProps<HourInternalProps>(hourElement, {
                segment,
              }) || <></>
            );
          case 'minute':
            return (
              applyInternalProps<MinuteInternalProps>(minuteElement, {
                segment,
              }) || <></>
            );
          case 'dayPeriod':
            return (
              applyInternalProps<PeriodInternalProps>(periodElement, {
                segment,
              }) || <></>
            );
          case 'literal':
            return <DateSegmentRAC segment={segment} />;
          default:
            return <></>;
        }
      }}
    </DateInputRAC>
  );
};

type HourProps = {
  className?: string;
};

type HourInternalProps = {
  segment: DateSegment;
};

const Hour = ({ className, ...internal }: HourProps) => {
  const { id, hidePlaceholder } = useFieldPropsV2<TimeInputFieldProps>();
  const { segment } = internal as HourInternalProps;
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useFocusFieldEvent(() => {
    ref.current?.focus();
  }, id);

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.hour');

  return (
    <DateSegmentRAC
      ref={ref}
      segment={segment}
      className={className}
      {...(placeholder && { placeholder })}
    />
  );
};

type MinuteProps = {
  className?: string;
};

type MinuteInternalProps = {
  segment: DateSegment;
};

const Minute = ({ className, ...internal }: MinuteProps) => {
  const { hidePlaceholder } = useFieldPropsV2<TimeInputFieldProps>();
  const { segment } = internal as MinuteInternalProps;
  const { t } = useTranslation();

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.minute');

  return (
    <DateSegmentRAC
      segment={segment}
      className={className}
      {...(placeholder && { placeholder })}
    />
  );
};

type PeriodProps = {
  className?: string;
};

type PeriodInternalProps = {
  segment: DateSegment;
};

const Period = ({ className, ...internal }: PeriodProps) => {
  const { hidePlaceholder } = useFieldPropsV2<TimeInputFieldProps>();
  const { segment } = internal as PeriodInternalProps;
  const { t } = useTranslation();

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.period');

  return (
    <DateSegmentRAC
      segment={segment}
      className={className}
      {...(placeholder && { placeholder })}
    />
  );
};

Time.Hour = Hour;
Time.Minute = Minute;
Time.Period = Period;

TimeInput.Label = Label;
TimeInput.Time = Time;
TimeInput.Description = Description;
TimeInput.Error = Error;

export { TimeInput };

function stringToTimeValue(value: string | null | undefined): TimeValue | null {
  if (!value) {
    return null;
  }
  try {
    return parseTime(value);
  } catch {
    return null;
  }
}

function timeValueToString(time: TimeValue | null): string | null {
  if (!time) {
    return null;
  }
  const hour = String(time.hour).padStart(2, '0');
  const minute = String(time.minute).padStart(2, '0');
  const second = String(time.second).padStart(2, '0');

  return [hour, minute, second].join(':');
}
